import React from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';

type IconLinkVariant = 'primary' | 'error' | 'warning' | 'success' | 'info';

type IconLinkProps = {
    icon: string;
    children: React.ReactNode;
    variant?: IconLinkVariant;
    dataTest?: string;
    tooltip?: string;
    indent?: number;
} & (
    | { href: string; onClick?: never; target?: string; disabled?: never }
    | { href?: never; onClick: () => void; target?: never; disabled?: never }
    | { href?: never; onClick?: never; target?: never; disabled: true }
);

export default function IconLink({
    href,
    icon,
    children,
    variant = 'primary',
    dataTest,
    tooltip,
    onClick,
    disabled,
    target = '_blank',
    indent = 1,
}: IconLinkProps) {
    const link = (
        <Box
            component={href && !disabled ? 'a' : 'span'}
            href={disabled ? undefined : href}
            target={href && !disabled ? target : undefined}
            rel={href && !disabled ? 'noopener' : undefined}
            onClick={disabled ? undefined : onClick}
            data-test={dataTest}
            aria-disabled={disabled || undefined}
            sx={{
                color: (theme) => (disabled ? theme.palette.text.disabled : theme.palette[variant].main),
                cursor: disabled ? 'default' : 'pointer',
                textDecoration: 'none',
                '&:hover': { textDecoration: disabled ? 'none' : 'underline' },
            }}
        >
            <Stack direction="row" spacing={indent} alignItems="center">
                <IconMi icon={icon} sx={{ fontSize: '16px' }} />
                <Box>{children}</Box>
            </Stack>
        </Box>
    );

    // A disabled link takes no pointer events of its own, so the tooltip needs an inline-flex wrapper
    // to keep receiving hover; tabIndex keeps it reachable by keyboard once the anchor is gone
    return tooltip ? (
        <Tooltip title={tooltip}>
            <Box tabIndex={disabled ? 0 : undefined} sx={{ display: 'inline-flex' }}>
                {link}
            </Box>
        </Tooltip>
    ) : (
        link
    );
}
